import { v4 as uuidv4 } from "uuid";
/**
 * Simple Session Manager
 * Just tracks if we're in an active session to prevent duplicate events.
 */

// Session timeout in milliseconds (30 minutes)
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

// Prefix for session keys to make them easily identifiable
export const SESSION_KEY_PREFIX = "Kismet_session_";

/**
 * Interface for the session object
 */
export interface Session {
  sessionStart: number;
  lastEvent: number;
  clientId: string;
  pre_UMP_ID?: string;
  UMP_ID?: string;
}

/**
 * Checks if we have an active session for this client
 * @param clientId The client ID from KismetConfig
 * @returns Whether an active session exists
 */
export function hasActiveClientSession(clientId: string): Session | null {
  try {
    const key = `${SESSION_KEY_PREFIX}${clientId}`;
    const session = sessionStorage.getItem(key);

    if (!session) {
      console.debug(`No session found for client ${clientId}`);
      return false;
    }

    // Check if session is still valid
    const sessionData = JSON.parse(session);
    const now = Date.now();

    // Use the lastEvent time if available, otherwise fall back to sessionStart
    const lastActivityTime = sessionData.lastEvent || sessionData.sessionStart;
    return now - lastActivityTime < SESSION_TIMEOUT_MS;
  } catch (error) {
    console.error("Session check failed:", error);
    return false;
  }
}

/**
 * Creates a new session for this client
 * @param clientId The client ID from KismetConfig
 */
export function createSession(clientId: string): Session {
  try {
    const key = `${SESSION_KEY_PREFIX}${clientId}`;
    const currentTime = Date.now();

    // We want to go ahead and instantiate a pre-upm_id, which we will then substitute a real upmid for on the session.
    const session: Session = {
      sessionStart: currentTime,
      lastEvent: currentTime,
      clientId,
      pre_UMP_ID: uuidv4(),
    };

    sessionStorage.setItem(key, JSON.stringify(session));
    return session;
  } catch (error) {
    console.error("Failed to create session:", error);
    // Return a default session object to satisfy the return type
    const currentTime = Date.now();
    return {
      sessionStart: currentTime,
      lastEvent: currentTime,
      clientId,
      pre_UMP_ID: uuidv4(),
    };
  }
}

/**
 * Updates the lastEvent timestamp for a session
 * @param clientId The client ID to update the session for
 * @returns Boolean indicating if the update was successful
 */
export function updateSessionLastEvent(clientId: string): boolean {
  try {
    const key = `${SESSION_KEY_PREFIX}${clientId}`;
    const sessionData = sessionStorage.getItem(key);

    if (!sessionData) {
      console.warn(`No session found for client ${clientId}`);
      return false;
    }

    // Parse the existing session
    const session = JSON.parse(sessionData) as Session;

    // Update just the lastEvent timestamp
    session.lastEvent = Date.now();

    // Save it back to session storage
    sessionStorage.setItem(key, JSON.stringify(session));
    return session;
  } catch (error) {
    console.error("Failed to update session lastEvent:", error);
    return false;
  }
}

/**
 * Updates the session with a UMP ID
 * @param clientId The client ID
 * @param umpId The User Marketing Profile ID to add
 * @returns The updated session or false if failed
 */
export function updateSessionUMPID(
  clientId: string,
  umpId: string
): Session | boolean {
  try {
    const key = `${SESSION_KEY_PREFIX}${clientId}`;
    const sessionData = sessionStorage.getItem(key);

    if (!sessionData) {
      console.warn(`No session found for client ${clientId}`);
      return false;
    }

    // Parse the existing session
    const session = JSON.parse(sessionData) as Session;

    // Update the UMP_ID
    session.UMP_ID = umpId;

    // Save it back to session storage
    sessionStorage.setItem(key, JSON.stringify(session));
    return session;
  } catch (error) {
    console.error("Failed to update session UMP_ID:", error);
    return false;
  }
}
